/**
 * Add a client.
 *
 * The interesting part is the failure handling. Three refusals are *expected*
 * here and each is a different sentence:
 *
 * - `VALIDATION_ERROR` — the PAN is malformed. The user's typo.
 * - `CONFLICT` — this firm already has that PAN. Their own record, safe to name.
 * - `USAGE_LIMIT_EXCEEDED` — the plan's client cap. **Not** an error; a
 *   commercial fact, and the screen must say so with a way forward
 *   (APP_DEVELOPMENT_GUIDE §7.3: render `SEAT_LIMIT_EXCEEDED` as "5 of 5 seats
 *   in use", not as "something went wrong").
 */

import { useState } from 'react'

import { ApiError, clients } from '../lib/api'
import { STORE_LISTING_URL } from '../lib/config'

/** Structural check, mirroring the service's. Shape only — whether the PAN
 *  exists or matches the legal name is the tax department's answer, not ours. */
const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/

export function NewClientDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: () => void
}) {
  const [legalName, setLegalName] = useState('')
  const [pan, setPan] = useState('')
  const [tradeName, setTradeName] = useState('')
  const [error, setError] = useState<{ message: string; atCap: boolean } | null>(null)
  const [saving, setSaving] = useState(false)

  const normalised = pan.trim().toUpperCase()
  const panLooksRight = PAN_RE.test(normalised)

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setSaving(true)
    setError(null)
    try {
      await clients.create({
        legal_name: legalName.trim(),
        pan: normalised,
        trade_name: tradeName.trim() || undefined,
      })
      onCreated()
    } catch (err) {
      if (err instanceof ApiError) {
        setError({ message: err.message, atCap: err.code === 'USAGE_LIMIT_EXCEEDED' })
      } else {
        setError({ message: 'The client could not be added.', atCap: false })
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <form
        onSubmit={submit}
        className="w-full max-w-md rounded-2xl border border-border bg-surface-raised p-6 shadow-xl"
      >
        <h2 className="mb-4 text-lg font-semibold text-foreground">Add a client</h2>

        <label className="mb-4 block">
          <span className="mb-1 block text-sm font-medium text-foreground">Legal name</span>
          <input
            value={legalName}
            onChange={(e) => setLegalName(e.target.value)}
            required
            maxLength={200}
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
          />
        </label>

        <label className="mb-4 block">
          <span className="mb-1 block text-sm font-medium text-foreground">PAN</span>
          <input
            value={pan}
            onChange={(e) => setPan(e.target.value)}
            required
            maxLength={10}
            placeholder="AAAPA1234A"
            className="w-full rounded-lg border border-border bg-background px-3 py-2 font-mono text-sm uppercase"
          />
          {normalised.length === 10 && !panLooksRight && (
            <span className="mt-1 block text-xs text-muted-foreground">
              That does not look like a valid PAN.
            </span>
          )}
        </label>

        <label className="mb-6 block">
          <span className="mb-1 block text-sm font-medium text-foreground">
            Trade name <span className="font-normal text-muted-foreground">(optional)</span>
          </span>
          <input
            value={tradeName}
            onChange={(e) => setTradeName(e.target.value)}
            maxLength={200}
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
          />
        </label>

        {error && (
          <div className="mb-4 rounded-lg border border-border bg-background p-3">
            <p className="text-sm text-foreground">{error.message}</p>
            {error.atCap && (
              <a
                href={STORE_LISTING_URL}
                className="mt-2 inline-block text-sm font-medium text-primary"
              >
                See plans with more clients →
              </a>
            )}
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-border px-4 py-2 text-sm font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || !panLooksRight || !legalName.trim()}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            {saving ? 'Adding…' : 'Add client'}
          </button>
        </div>
      </form>
    </div>
  )
}
